// Pantalla que ve quien ha iniciado sesión pero no tiene plan activo: nunca ha pagado, o canceló.
//
// No es un "plan gratis agotado": desde ago-2026 no hay plan gratis. Producir un vídeo exige guion
// y narración, las dos con coste de IA, así que no había forma honesta de regalar producción — y
// como se renderiza un vídeo cada vez, hacerlo habría sido a costa de la cola de quien paga.
//
// Deliberadamente NO es un callejón sin salida: desde aquí se puede contratar, escribir a soporte,
// leer la documentación y descargar los vídeos ya producidos, que son del usuario.
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Check, Loader2, LogOut, LifeBuoy, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useQuota } from "../context/QuotaContext";
import api, { apiErr } from "../lib/api";
import SeatBadge, { fmtWeekEnd } from "../components/SeatBadge";

const eur = (n) => `${Number(n).toLocaleString("es-ES")}€`;
const num = (n) => Number(n).toLocaleString("es-ES");

export default function Paywall() {
  const { user, logout } = useAuth();
  const { checkout } = useQuota();
  const [precios, setPrecios] = useState(null);
  const [busy, setBusy] = useState(null);
  const [seats, setSeats] = useState(null); // plazas restantes por plan — aviso ANTES de intentar pagar
  const [seatsFullPlan, setSeatsFullPlan] = useState(null); // red de seguridad: se agotaron justo al pulsar
  const [waitlisted, setWaitlisted] = useState(null);

  useEffect(() => {
    api.get("/public/pricing").then(({ data }) => setPrecios(data)).catch(() => setPrecios({ planes: [] }));
    api.get("/plan-seats").then((r) => setSeats(r.data?.plans || {})).catch(() => setSeats({}));
  }, []);

  const contratar = async (id) => {
    setBusy(id);
    setSeatsFullPlan(null);
    try {
      await checkout(id, "monthly");
    } catch (e) {
      // Antes esto no se capturaba: cualquier fallo de pago (plazas agotadas o cualquier otro
      // motivo) dejaba al usuario sin ningún aviso, solo el botón dejaba de girar.
      const d = e?.response?.data?.detail;
      if (d?.error === "seats_full") { setSeatsFullPlan(id); setWaitlisted(null); }
      toast.error(apiErr(e));
    } finally { setBusy(null); }
  };

  const joinWaitlist = async (id) => {
    if (!user?.email) return;
    setBusy(id);
    try {
      await api.post("/plan-seats/waitlist", { email: user.email, plan: id });
      setWaitlisted(id);
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(null);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-6 py-4">
          <img src="/ytmethod_transparent.png" alt="YTMethod" className="h-9 w-auto" />
          <span className="ml-auto hidden text-sm text-slate-400 sm:inline">{user?.email}</span>
          <button onClick={logout} data-testid="paywall-logout"
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-slate-500 hover:bg-slate-100">
            <LogOut size={16} /> Salir
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
          Elige un plan para empezar
        </h1>
        <p className="mt-3 max-w-2xl text-lg text-slate-600">
          Cada documental consume IA, procesamiento y almacenamiento. Por eso hace falta un plan
          activo — cancela cuando quieras.
        </p>

        {!precios ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={36} /></div>
        ) : (
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {(precios.planes || []).map((p) => (
              <div key={p.id} data-testid={`paywall-plan-${p.id}`}
                className={`relative flex flex-col rounded-2xl border-2 bg-white p-6 ${
                  p.id === "creator" ? "border-blue-600 shadow-lg" : "border-slate-200"}`}>
                {/* Absoluta y flotando por encima del borde (no dentro de la tarjeta): así no
                    empuja el título hacia abajo y las 4 tarjetas quedan con precios y textos
                    alineados en la misma altura. Mismo patrón que UpgradeModal.jsx. */}
                {p.id === "creator" && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-blue-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-md whitespace-nowrap">
                    Más popular
                  </div>
                )}
                <div className="text-lg font-bold text-slate-900">{p.label}</div>
                <div className="mt-2 text-4xl font-extrabold text-slate-900">
                  {eur(p.precio_eur)}<span className="text-base font-medium text-slate-500">/mes</span>
                </div>
                <ul className="mt-5 flex-1 space-y-2.5 text-[15px] text-slate-700">
                  <li className="flex gap-2"><Check size={18} className="mt-0.5 shrink-0 text-emerald-600" />
                    <span><b>{num(p.creditos)} créditos</b> al mes</span></li>
                  <li className="flex gap-2"><Check size={18} className="mt-0.5 shrink-0 text-emerald-600" />
                    <span>~{Math.round(p.videos_referencia)} vídeos de {precios.duracion_referencia_min} min</span></li>
                  <li className="flex gap-2"><Check size={18} className="mt-0.5 shrink-0 text-emerald-600" />
                    <span>Vídeos de hasta {p.max_minutos} min</span></li>
                  <li className="flex gap-2"><Check size={18} className="mt-0.5 shrink-0 text-emerald-600" />
                    <span>{p.canales} {p.canales === 1 ? "canal" : "canales"}</span></li>
                </ul>
                {/* Aviso previo de plazas: se oculta si el fallback reactivo de abajo ya está
                    mostrando el mismo aviso para este plan (se agotaron justo al pulsar
                    "elegir") — mostrar los dos a la vez duplicaría el mensaje. */}
                {seatsFullPlan !== p.id && (
                  <SeatBadge plan={p.id} info={seats?.[p.id]} weekEndLabel={fmtWeekEnd(seats?.[p.id]?.week_ends_at, "es")} email={user?.email} />
                )}
                {seatsFullPlan === p.id ? (
                  waitlisted === p.id ? (
                    <div data-testid={`waitlist-success-${p.id}`} className="mt-4 text-sm font-semibold text-emerald-700 bg-emerald-50 border-2 border-emerald-200 rounded-xl px-3 py-3 text-center">
                      ✅ Te avisaremos en cuanto se libere una plaza.
                    </div>
                  ) : (
                    <button onClick={() => joinWaitlist(p.id)} disabled={!!busy} data-testid={`join-waitlist-plan-${p.id}`}
                      className="mt-4 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl px-5 font-bold text-base text-white bg-amber-500 hover:bg-amber-600 disabled:opacity-60">
                      {busy === p.id ? <Loader2 size={18} className="animate-spin" /> : null}
                      Unirme a la lista de espera
                    </button>
                  )
                ) : (
                  <button onClick={() => contratar(p.id)} disabled={!!busy} data-testid={`paywall-cta-${p.id}`}
                    className={`mt-6 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl px-5 font-bold transition-colors disabled:opacity-60 ${
                      p.id === "creator" ? "bg-blue-600 text-white hover:bg-blue-700"
                                         : "border-2 border-blue-600 bg-white text-blue-700 hover:bg-blue-50"}`}>
                    {busy === p.id ? <Loader2 size={18} className="animate-spin" /> : null}
                    Elegir {p.label}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

      </main>

      {/* Los enlaces que antes vivían en la tarjeta "Mientras tanto" (documentación, soporte) se
          mudan aquí junto a los legales — es lo que se espera encontrar en un footer, no dentro
          de una tarjeta a mitad de página. Que no parezca un callejón sin salida sigue en pie:
          los vídeos ya producidos siguen siendo del usuario y puede seguir pidiendo ayuda. */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-6 py-8 flex flex-col sm:flex-row sm:items-center gap-4 text-sm">
          <p className="text-slate-500">© 2026 YTMethod. Todos los derechos reservados.</p>
          <div className="sm:ml-auto flex flex-wrap gap-x-6 gap-y-2 font-medium">
            <Link to="/docs" className="inline-flex items-center gap-1.5 text-slate-600 hover:text-slate-900">
              <BookOpen size={14} /> Documentación
            </Link>
            <Link to="/ayuda" className="inline-flex items-center gap-1.5 text-slate-600 hover:text-slate-900">
              <LifeBuoy size={14} /> Soporte
            </Link>
            <Link to="/privacidad" className="text-slate-600 hover:text-slate-900">Privacidad</Link>
            <Link to="/terminos" className="text-slate-600 hover:text-slate-900">Términos</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
